import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  Typography,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";

import CustomCheckbox from "../components/CustomCheckbox";


/* ---------------- FIXED ROLES ---------------- */
const roles = [
  "Company Admin",
  "Admin",
  "Warehouse Manager",
  "Loading Manager",
  "Driver App",
  "Salesman App",
  "Technician App",
  "Customer App",
];

/* ---------------- DYNAMIC COMPANIES ---------------- */
const companies = Array.from({ length: 12 }).map((_, i) => ({
  id: `company_${i}`,
  name: `Company ${i + 1}`,
}));

/* ---------------- MODULES PER ROLE ---------------- */
const roleModules = {
  "Company Admin": [
    { id: "users", name: "User Management" },
    { id: "roles", name: "Roles & Permissions" },
    { id: "billing", name: "Billing" },
    { id: "settings", name: "Company Settings" },
    { id: "billing1", name: "Billing" },
    { id: "billing2", name: "Billing" },
    { id: "billing4", name: "Billing" },
    { id: "billing5", name: "Billing" },
    { id: "billing6", name: "Billing" },
    { id: "billing7", name: "Billing" },
    { id: "billing8", name: "Billing" },
    { id: "billing9", name: "Billing" },
    { id: "billing10", name: "Billing" }
  ],
  Admin: [
    { id: "dashboard", name: "Dashboard" },
    { id: "reports", name: "Reports" },
  ],
  "Warehouse Manager": [
    { id: "stock", name: "Stock" },
    { id: "inward", name: "Inward" },
    { id: "outward", name: "Outward" },
  ],
  "Driver App": [
    { id: "trip", name: "Trips" },
    { id: "pod", name: "POD Upload" },
  ],
};

/* ---------------- PAGE ---------------- */
export default function AccessRights() {
  const [activeRole, setActiveRole] = useState(0);
  const [permissions, setPermissions] = useState({});

  const activeRoleName = roles[activeRole];
  const modules = roleModules[activeRoleName] || [];

  /* 🔥 FORCE WIDTH: Module + (View+Edit per company) */
  const tableMinWidth = useMemo(() => {
    const MODULE_COL = 300;
    const PER_COMPANY = 150; // View + Edit
    return MODULE_COL + companies.length * PER_COMPANY;
  }, []);

const togglePermission = (companyId, moduleId, type) => {
  setPermissions((prev) => {
    const rolePerms = prev[activeRoleName] || {};
    const companyPerms = rolePerms[companyId] || {};
    const modulePerms = companyPerms[moduleId] || {};

    let updated = { ...modulePerms };

    if (type === "edit") {
      const newEdit = !modulePerms.edit;
      updated.edit = newEdit;

      // 🔥 RULE 1: Edit implies View
      if (newEdit) {
        updated.view = true;
      }
    }

    if (type === "view") {
      const newView = !modulePerms.view;
      updated.view = newView;

      // 🔥 RULE 2: No View → No Edit
      if (!newView) {
        updated.edit = false;
      }
    }

    return {
      ...prev,
      [activeRoleName]: {
        ...rolePerms,
        [companyId]: {
          ...companyPerms,
          [moduleId]: updated,
        },
      },
    };
  });
};



  return (

     <Box className="st-page">
          {/* ================= PAGE HEADER ================= */}
          <Box className="st-page-header">
            <Typography component="div" className="st-h1">
              Access Rights Management
            </Typography>
            <Typography component="div" className="st-h2 st-subtitle">
              View and modify module permissions for each company. Use the toggle Button to set view, edit remove rights
            </Typography>
          </Box>


    <Card className="st-card">
      {/* ---------- ROLE TABS ---------- */}
      <Tabs
        value={activeRole}
        onChange={(_, v) => setActiveRole(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ px: 2, mb: 2 }}
      >
           {roles.map((r, index) => (
              <Tab
                key={r}
                value={index}   // ✅ ADD THIS
                label={
                  <span>
                    {r.split(" ").map((word, i) => (
                      <span key={i} style={{ display: "block" }}>
                        {word}
                      </span>
                    ))}
                  </span>
                }
                className="st-h2 tabs-access-rights"
              />
            ))}
      </Tabs>

      {/* =====================================================
          🔥 HORIZONTAL SCROLL ZONE (TABLE ONLY)
         ===================================================== */}
{/* ================= ACCESS RIGHTS TABLE ================= */}
<Box
  sx={{
    display: "flex",
    width: "100%",
    overflow: "hidden", // ❌ NO SCROLL HERE
  }}
>
  {/* ========== LEFT FIXED TABLE (MODULE) ========== */}
<TableContainer
  className="access-left-table"
  sx={{
    width: {xs:200, md:250},
    flexShrink: 0,
  }}
>
  <Table >
      <TableHead>
        <TableRow>
          <TableCell
            rowSpan={2}                 // 🔑 merge two header rows
            align="center"
            className="st-h2"
            sx={{
              verticalAlign: "middle",  // center text vertically
              backgroundColor: "#fff",
            }}
          >
            Module
          </TableCell>
        </TableRow>

        {/* second header row exists implicitly for alignment */}
        <TableRow />
      </TableHead>

    <TableBody>
      {modules.map((m) => (
        <TableRow key={`${activeRoleName}-${m.id}`}>
          <TableCell className="st-h3" align="center">
            {m.name}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
</TableContainer>


  {/* ========== RIGHT SCROLLABLE TABLE (COMPANIES ONLY) ========== */}
<TableContainer
  className="access-right-table"
  sx={{
    overflowX: "auto",
    width: "100%",
  }}
>
  <Table
    stickyHeader
    className="access-rights-table"
    sx={{ minWidth: tableMinWidth }}
  >
    <TableHead>
      {/* COMPANY NAMES */}
      <TableRow>
        {companies.map((c) => (
          <TableCell
            key={c.id}
            colSpan={2}
            align="center"
            className="st-h2"
          >
            {c.name}
          </TableCell>
        ))}
      </TableRow>

      {/* VIEW / EDIT */}
      <TableRow>
        {companies.map((c) => (
          <React.Fragment key={c.id}>
            <TableCell className="st-h3" align="center">
              View
            </TableCell>
            <TableCell className="st-h3" align="center">
              Edit
            </TableCell>
          </React.Fragment>
        ))}
      </TableRow>
    </TableHead>

    <TableBody>
      {modules.map((m) => (
        <TableRow key={`${activeRoleName}-${m.id}`}>
          {companies.map((c) => (
            <React.Fragment key={c.id}>
              <TableCell align="center">
                <CustomCheckbox
                  size="small"
                  checked={
                    permissions?.[activeRoleName]?.[c.id]?.[m.id]?.view ||
                    false
                  }
                  onChange={() =>
                    togglePermission(c.id, m.id, "view")
                  }
                />
              </TableCell>

              <TableCell align="center">
                <CustomCheckbox
                  size="small"
                  checked={
                    permissions?.[activeRoleName]?.[c.id]?.[m.id]?.edit ||
                    false
                  }
                  onChange={() =>
                    togglePermission(c.id, m.id, "edit")
                  }
                />
              </TableCell>
            </React.Fragment>
          ))}
        </TableRow>
      ))}
    </TableBody>
  </Table>
</TableContainer>

</Box>

    </Card>
    </Box>
  );
}
